import * as React from "react";

import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { IDatasheetResultElement } from "@modules/datasheets/components/result/types/datasheet-element";
import { getCommonFieldBasedElementParams, useDatasheetElementController } from "@modules/datasheets/components/result/controllers/datasheet-element";
import { isGTRSystemField } from "@modules/datasheets/utils/gtr-fields";
import { useDatasheetNavigationContext, useDatasheetResultFieldEditable } from '../../context';

export type TextFieldProps = IDatasheetResultElement<DatasheetFieldComponentGetDto>;

export const useTextFieldController = (props: TextFieldProps) => {
  const { datasheetElement, onChange } = props;
  const fieldId = datasheetElement.original.field.fieldid ?? datasheetElement.original.field.id;
  const isGTR = isGTRSystemField(fieldId);
  
  // Use field.name if explicitly set (including empty string), otherwise use displayValue
  const fieldName = (datasheetElement.original.field as any).name;
  const displayValue = (datasheetElement.original as any).displayValue ?? '';
  const initialValue = fieldName !== undefined ? fieldName : displayValue;
  const [textFieldValue, setTextFieldValue] = React.useState(initialValue);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const fieldContainerRef = React.useRef<HTMLDivElement>(null);
  const originalWidth = datasheetElement.original.width;
  const originalHeight = datasheetElement.original.height;

  // Resizable when focused; reset to original size on blur
  const [isFocused, setIsFocused] = React.useState(false);
  // Track current dimensions when focused so React doesn't overwrite user's resize
  const [currentWidth, setCurrentWidth] = React.useState(originalWidth);
  const [currentHeight, setCurrentHeight] = React.useState(originalHeight);

  // Only update dimensions when user actually resizes (observed size differs from original)
  React.useEffect(() => {
    if (!isFocused || !fieldContainerRef.current) return;
    const el = fieldContainerRef.current;
    const ro = new ResizeObserver(() => {
      // Use border box (offsetWidth/offsetHeight) so we don't shrink due to contentRect excluding border
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (w > 0 && h > 0 && (w !== originalWidth || h !== originalHeight)) {
        setCurrentWidth(w);
        setCurrentHeight(h);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [isFocused, originalWidth, originalHeight]);

  // Sync current dimensions when original changes (e.g. format update)
  React.useEffect(() => {
    if (!isFocused) {
      setCurrentWidth(originalWidth);
      setCurrentHeight(originalHeight);
    }
  }, [originalWidth, originalHeight, isFocused]);

  React.useEffect(() => {
    // Get the actual saved value from field.name, not displayValue
    // This ensures that when a value is deleted (empty string), it stays deleted
    const fieldName = (datasheetElement.original.field as any).name;
    const currentDisplayValue = (datasheetElement.original as any).displayValue ?? '';
    // Use field.name if it's explicitly set (including empty string), otherwise fall back to displayValue
    // Check if field.name exists in the object (even if it's empty string) to distinguish from undefined
    const valueToUse = fieldName !== undefined ? fieldName : currentDisplayValue;
    if (textFieldValue !== valueToUse) {
      setTextFieldValue(valueToUse);
    }
  }, [(datasheetElement.original as any).displayValue, (datasheetElement.original.field as any).name]);

  const { 
    headingSizeDependencies, 
    headingPlacedHorizontally, 
    headingPosition 
  } = getCommonFieldBasedElementParams(props);

  const baseDatasheetElementController = useDatasheetElementController(
    props,
    { headingSizeDependencies }
  );

  const handleTextFieldValueChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTextFieldValue(e.target.value);
  }, []);

  const handleTextFieldValueSave = React.useCallback(() => {
    onChange(datasheetElement.id, {
      ...datasheetElement,
      original: {
        ...datasheetElement.original,
        field: {
          ...datasheetElement.original.field,
          name: textFieldValue,
        },
      },
    });
  }, [textFieldValue, datasheetElement, onChange]);

  const handleFocus = React.useCallback(() => {
    setIsFocused(true);
  }, []);

  const { registerField, focusNext, focusPrev } = useDatasheetNavigationContext();
  const elementId = datasheetElement.id;
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  const isEditable = useDatasheetResultFieldEditable(datasheetElement.original.editable !== false);

  React.useEffect(() => {
    if (!isEditable) return;
    registerField(elementId, { focus: () => inputRef.current?.focus() });
    return () => { registerField(elementId, null); };
  }, [elementId, registerField, isEditable]);

  const handleTabNavigation = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key !== 'Tab') return;
      e.preventDefault();
      if (e.shiftKey) {
        focusPrev(elementId);
      } else {
        focusNext(elementId);
      }
    },
    [elementId, focusNext, focusPrev],
  );

  const handleBlur = React.useCallback(() => {
    setIsFocused(false);
    setCurrentWidth(originalWidth);
    setCurrentHeight(originalHeight);
    if (isEditable) {
      handleTextFieldValueSave();
    }
    // Reset to original size when user clicks outside
    if (fieldContainerRef.current) {
      fieldContainerRef.current.style.width = `${originalWidth}px`;
      fieldContainerRef.current.style.height = `${originalHeight}px`;
    }
  }, [originalWidth, originalHeight, handleTextFieldValueSave, isEditable]);

  const fieldWidth = isFocused ? currentWidth : originalWidth;
  const fieldHeight = isFocused ? currentHeight : originalHeight;

  return {
    handleTextFieldValueSave,
    handleTextFieldValueChange,
    headingPosition,
    headingPlacedHorizontally,
    textFieldValue,
    isGTR,
    isFocused,
    fieldWidth,
    fieldHeight,
    containerRef,
    fieldContainerRef,
    handleFocus,
    handleBlur,
    inputRef,
    handleTabNavigation,
    isEditable,
    ...baseDatasheetElementController,
  };
};